import { useContext, useState } from 'react';
import { Modal, Button, Group, Stack, Select, TextInput, Textarea, Alert } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { DocumentReference, Timestamp, updateDoc } from 'firebase/firestore';
import { functions, useFirestore } from '../../helpers/firebaseContext';
import { KYC, CompanyAuthorizedSignatories, Customer, UserDataType, CustomClaims, BaseResult, Action } from '../../types';
import { MyUserDataContext } from '../../contexts';
import { httpsCallable } from 'firebase/functions';
import { FirebaseError } from 'firebase/app';
import { fromMantineDate, toMantineDate } from '../../helpers/mantineDates';

interface InterviewFormModalProps {
    opened: boolean;
    onClose: () => void;
    kycRef: DocumentReference;
    customerData: Customer;
    signatories?: CompanyAuthorizedSignatories[];
    existing?: KYC['interviewFormData'];
}

export const InterviewFormModal = ({
    opened,
    onClose,
    kycRef,
    customerData,
    signatories = [],
    existing,
}: InterviewFormModalProps) => {
    const db = useFirestore();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const userData: UserDataType = useContext(MyUserDataContext);
    let userClaims = userData.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const kycAction = httpsCallable<Action, BaseResult>(functions, "kycAction");
    const form = useForm({
        initialValues: {
            interviewType: existing?.interviewType ?? '' as 'in_person' | 'virtual' | 'phone' | '',
            interviewAddress: existing?.interviewAddress ?? '',
            interviewBy: userData.user?.email ?? userData.user?.uid,
            interviewDate: existing?.interviewDate instanceof Timestamp
                ? toMantineDate(existing.interviewDate.toDate())
                : null as string | null,
            entityRequirements: existing?.entityRequirements ?? '',
            generalRemarks: existing?.generalRemarks ?? '',
            intervieweeID: existing?.intervieweeID ?? '',
        },
        validate: {
            interviewType: (v) => (!v ? 'Required' : null),
            interviewDate: (v) => (!v ? 'Required' : null),
            interviewAddress: (v) => (!v ? 'Required' : null),
            entityRequirements: (v) => (!v ? 'Required' : null),
            generalRemarks: (v) => (!v ? 'Required' : null),
            intervieweeID: (v) => (signatoryOptions.length > 0 && !v ? 'Required' : null),
            interviewBy: (v) => (!v ? 'Required' : null),
        },
    });

    const signatoryOptions = signatories.map((s) => ({
        value: s.id,
        label: s.customer?.name_en ?? s.customer?.name_ar ?? s.id,
    }));

    const handleSubmit = async (values: typeof form.values) => {
        setLoading(true);
        setError(null);
        try {
            const interviewFormData = {
                interviewType: values.interviewType || null,
                interviewAddress: values.interviewAddress || null,
                interviewBy: values.interviewBy || null,
                interviewDate: values.interviewDate ? Timestamp.fromDate(fromMantineDate(values.interviewDate)!) : null,
                entityRequirements: values.entityRequirements || null,
                generalRemarks: values.generalRemarks || null,
                intervieweeID: values.intervieweeID || null,
            };


            // Get token first
            let token = userClaims?.kfh_rm_approver ? "" : await userData.getAzureADToken!(
                userData
            );

            if (!token && !userClaims?.kfh_rm_approver) {
                throw new FirebaseError(
                    "no_microsoft_token",
                    "No Microsoft token.  Try refreshing"
                );
            }


            const res = await kycAction({
                action: 'interview_form_data_added',
                kycID: kycRef.id,
                customerID: customerData?.id,
                token: token || "",
            });

            if (res.data.status !== "success") {
                throw new Error(res.data.message || "Failed to add interview form data");
            }

            // Only save if action succeeded
            const cleaned = Object.fromEntries(
                Object.entries(interviewFormData).filter(([, v]) => v !== null)
            );
            await updateDoc(kycRef, { interviewFormData: cleaned });

            onClose();
        } catch (e: any) {
            setError(e.message ?? 'Something went wrong.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={existing ? 'Edit Interview Form Data' : 'Add Interview Form Data'}
            size="md"
        >
            <form onSubmit={form.onSubmit(handleSubmit)}>
                <Stack gap="sm">
                    {error && <Alert color="red">{error}</Alert>}

                    <Select
                        label="Interview Type"
                        placeholder="Select type"
                        data={[
                            { value: 'in_person', label: 'In Person' },
                            { value: 'virtual', label: 'Virtual' },
                            { value: 'phone', label: 'Phone' },
                        ]}
                        {...form.getInputProps('interviewType')}
                    />

                    <DatePickerInput
                        label="Interview Date"
                        valueFormat="DD MMM YYYY"
                        {...form.getInputProps('interviewDate')}
                    />

                    <TextInput
                        label="Interview Address"
                        placeholder="Address or meeting link"
                        {...form.getInputProps('interviewAddress')}
                    />

                    {signatoryOptions.length > 0 && (
                        <Select
                            label="Interviewee (Authorized Signatory)"
                            placeholder="Select signatory"
                            clearable
                            data={signatoryOptions}
                            {...form.getInputProps('intervieweeID')}
                        />
                    )}

                    <Textarea
                        label="Entity Requirements"
                        placeholder="Enter entity requirements"
                        minRows={2}
                        autosize
                        {...form.getInputProps('entityRequirements')}
                    />

                    <Textarea
                        label="General Remarks"
                        placeholder="Enter general remarks"
                        minRows={2}
                        autosize
                        {...form.getInputProps('generalRemarks')}
                    />

                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={onClose} disabled={loading}>Cancel</Button>
                        <Button type="submit" loading={loading}>
                            {existing ? 'Update' : 'Save'}
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
};
